/* eslint-disable react/no-danger */
import Icon from '@mdi/react';
import { pdsTv } from '@nestle-npus-nbm/pds-ui';
import { AnimatePresence, motion } from 'framer-motion';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import { merge } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { mdiOpenInNew } from 'src/assets/icons/mdiIcons';
import { CardLink } from 'src/components/Link';
import { CmsButton } from 'src/components/button/CmsButton';
import { imageMatteTv, themeStyles } from 'src/theme/basic-styles';
import { CMS_THEME } from '../../common/enums';
import { getMedia } from '../../utils/mediaHelpers';
import { StorageContentBlock } from '../common/StorageContentBlock/StorageContentBlock';

const gridTv = pdsTv({
  base: 'pds-grid pds-grid-cols-2 pds-gap-x-4 pds-gap-y-5 pds-p-0 lg:pds-grid-cols-4',
});

const BrandCardItem = React.forwardRef(
  (
    el: IStorageBrandCard & { mode?: string; cmsTheme: CMS_THEME },
    ref: React.Ref<HTMLAnchorElement>,
  ): JSX.Element => {
    const handleGA = () => {
      sendIt({
        event: 'module_interaction',
        eventParams: {
          component_id: `primary_cta: ${el.text}`,
          component_type: `brand_card: ${el.text}`,
          content_name: el.text,
        },
      });
    };

    const imageMatteStyles = useMemo(
      () => imageMatteTv({ theme: el.cmsTheme, className: 'pds-py-4' }),
      [el.cmsTheme],
    );
    const isTargetBlank = el.link.options?.attributes?.target === '_blank';

    return (
      <CardLink
        className="pds-flex pds-h-full pds-w-full pds-flex-col pds-justify-center pds-rounded pds-no-underline"
        id={el.component_id}
        to={el.link.url}
        onClick={handleGA}
        analytics={false}
        theme={el.cmsTheme}
        ref={ref}
        hideExternalIcon
        {...el.link.options?.attributes}
      >
        <div className={imageMatteStyles}>
          {el.relationships?.field_media &&
            getMedia({
              media: el.relationships?.field_media,
              objectFit: 'contain',
              style: { width: '100%', height: '100px' },
              'aria-hidden': true,
              alt: '',
            })}
        </div>
        <span className="pds-my-4 pds-grow pds-text-center pds-text-body-md">
          {el.text}&nbsp;
          {isTargetBlank && <Icon title="Open in new window" path={mdiOpenInNew} size={0.6} />}
        </span>
      </CardLink>
    );
  },
);

type Props = {
  node: IStorageBrandGrid;
};

const DefaultSettings = {
  entity_heading: {
    element: 'h2',
    style: 'typography_h2',
  },
  entity_variant: {
    load_more: false,
    theme: CMS_THEME.DARK,
    text_align: 'center',
  },
};

const StorageBrandGrid = ({ node: brandGrid }: Props) => {
  const { t } = useTranslation();
  const {
    relationships: { cards },
  } = brandGrid;
  const settings = merge({}, DefaultSettings, brandGrid?.behaviors);

  const loadMore = settings.entity_variant.load_more;
  const [visibleCards, setVisibleCards] = React.useState(loadMore ? 4 : cards.length);
  const prevVisibleCardsRef = useRef(visibleCards);
  const cmsTheme: CMS_THEME = settings.entity_variant.theme;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const currentRowFirstElementRef = useRef<HTMLAnchorElement | null>(null);

  const gridStyles = useMemo(() => gridTv(), []);

  useEffect(() => {
    if (loadMore && visibleCards > prevVisibleCardsRef.current) {
      currentRowFirstElementRef.current?.focus();
    } else if (loadMore && visibleCards < prevVisibleCardsRef.current) {
      buttonRef.current?.focus();
    }
    prevVisibleCardsRef.current = visibleCards;
  }, [visibleCards, loadMore]);

  const handleShowMore = useCallback(() => {
    setVisibleCards(prev => {
      const newVisibleCount = prev >= cards.length ? 4 : Math.min(prev + 4, cards.length);
      return newVisibleCount;
    });
  }, [cards.length]);

  if (!cards || cards.length === 0) return null;

  return (
    <div
      className={themeStyles({ theme: cmsTheme, className: 'pds-py-4.5 lg:pds-py-6' })}
      data-id={brandGrid.drupal_id}
    >
      <Container className="!pds-max-w-site-container pds-text-center">
        {(brandGrid?.title || brandGrid?.body?.processed) && (
          <div className="pds-mb-4.5 md:pds-mb-5.5">
            <StorageContentBlock node={{ ...brandGrid, behaviors: settings }} />
          </div>
        )}
        <ul className={gridStyles} key="brand-grid-desktop">
          <AnimatePresence>
            {cards.map(
              (element, i) =>
                i < visibleCards && (
                  <motion.li
                    key={element.id}
                    initial={i >= 4 && loadMore && { opacity: 0, height: '0px' }}
                    animate={
                      i >= 4 &&
                      loadMore && {
                        opacity: 1,
                        height: 'max-content',
                        transition: { duration: 0.3, delay: i * 0.1 },
                      }
                    }
                    exit={{
                      opacity: 0,
                      translateY: '-10%',
                      transition: { duration: 0.3, delay: (visibleCards - i) * 0.1 },
                    }}
                  >
                    <BrandCardItem
                      {...element}
                      ref={el => {
                        // Set reference to the first item of the current row
                        if (i % 4 === 0 && i < visibleCards) {
                          currentRowFirstElementRef.current = el;
                        }
                      }}
                      cmsTheme={cmsTheme}
                      mode={cmsTheme}
                    />
                  </motion.li>
                ),
            )}
          </AnimatePresence>
        </ul>
        {loadMore && cards.length > 4 && (
          <div className="pds-mt-4.5 pds-flex pds-justify-center">
            <CmsButton
              ref={buttonRef}
              cmsTheme={cmsTheme}
              onClick={handleShowMore}
              className="pds-w-full md:pds-w-auto"
            >
              {visibleCards >= cards.length ? t('Show Fewer') : t('View More')}
            </CmsButton>
          </div>
        )}
      </Container>
    </div>
  );
};

export default StorageBrandGrid;
